import { LockStatus, FailedAction } from './DownTimeEnums'
import { ValidationResults, ValidationMessage } from './ValidationResults'

export enum DowntimePropertyName {
  TimeOff = 'TimeOff',
  TimeOn = 'TimeOn',
  Group = 'Group',
  SubGroup = 'SubGroup',
  AlarmCode = 'AlarmCode',
  PenalisingStatus = 'PenalisingStatus'
}

export class DownTime implements TimeOnOff {
  public constructor(init?: Partial<DownTime>, siteKey?: string) {
    if (!!!init) {
      if (!siteKey) {
        throw new Error('Trying to create a new downtime event with no siteKey')
      }
      this.timeOn = new Date()
      this.siteKey = siteKey
      this.saveInFlight = false
      this.isNew = true
    } else {
      Object.assign(this, init)
      if (!init.timeOn) {
        throw new Error('Trying to create downtime event with no timeon')
      }
      // convert json strings to dates
      this.timeOn = new Date(init.timeOn)
      this.timeOff = init.timeOff ? new Date(init.timeOff) : undefined
      this.dateLastModified = init.dateLastModified ? new Date(init.dateLastModified) : undefined
      this.parentEventTimeOn = init.parentEventTimeOn ? new Date(init.parentEventTimeOn) : undefined

      // convert nulls in json to undefined
      this.lostProduction = init.lostProduction === null ? undefined : init.lostProduction
      this.meanWindSpeed = init.meanWindSpeed === null ? undefined : init.meanWindSpeed
      this.windSpeedDataCoverage =
        init.windSpeedDataCoverage === null ? undefined : init.windSpeedDataCoverage

      if (!init.isNew) {
        // id must be a property, not a method because it is accessed directly by ReactTable
        this.id = this.scadaObjectKey + this.timeOn
      }
    }

    // these methods must be assigned after Object.assign otherwise 'this' will be bound to the init object
    this.Edited = (): boolean => this.parentEventTimeOn !== undefined

    // tell if any editable fields have changed
    this.HasBeenEdited = (original: DownTime): boolean =>
      this.comment !== original.comment ||
      this.group !== original.group ||
      this.subGroup !== original.subGroup ||
      this.penalisingStatus !== original.penalisingStatus

    this.IsValid = (): boolean => {
      let validationMessages: ValidationMessage[] = []
      if (this.validation) {
        validationMessages.push(...(this.validation.errors ? this.validation.errors : []))
        validationMessages.push(...(this.validation.information ? this.validation.information : []))
        validationMessages.push(...(this.validation.warnings ? this.validation.warnings : []))
      }
      return validationMessages.length === 0
    }

    this.GetAllValidationMessagesForProperty = (property: string): ValidationMessage[] => {
      let validationMessages: ValidationMessage[] = []
      if (this.validation) {
        validationMessages.push(
          ...this.GetValidationMessagesForProperty(property, this.validation.errors)
        )
        validationMessages.push(
          ...this.GetValidationMessagesForProperty(property, this.validation.information)
        )
        validationMessages.push(
          ...this.GetValidationMessagesForProperty(property, this.validation.warnings)
        )
      }
      return validationMessages
    }

    this.RemoveAllValidationMessagesForProperty = (property: string): void => {
      if (this.validation) {
        this.validation.errors = this.RemoveValidationMessagesForProperty(
          property,
          this.validation.errors
        )
        this.validation.information = this.RemoveValidationMessagesForProperty(
          property,
          this.validation.information
        )
        this.validation.warnings = this.RemoveValidationMessagesForProperty(
          property,
          this.validation.warnings
        )
      }
    }

    this.AddValidationMessages = (validationMessages: ValidationMessage[]): void => {
      if (this.validation) {
        this.validation.errors
          ? this.validation.errors.push(...validationMessages)
          : (this.validation.errors = validationMessages)
      } else {
        this.validation = new ValidationResults()
        this.validation.errors = validationMessages
      }
    }

    this.SetValidationMessagesForProperties = (
      validationMessages: ValidationMessage[],
      properties: string[]
    ): void => {
      properties.map(p => this.RemoveAllValidationMessagesForProperty(p))
      this.AddValidationMessages(validationMessages)
    }

    this.DurationInMiliSeconds = (toDate: Date, fromDate: Date): number => {
      const part1: number = Math.min(
        this.timeOff ? new Date(this.timeOff).getTime() : new Date().getTime(),
        toDate.getTime()
      )
      const part2 = Math.max(new Date(this.timeOn).getTime(), fromDate.getTime())
      return part1 - part2
    }
  }

  RemoveValidationMessagesForProperty = (
    property: string,
    validationMessages: ValidationMessage[] | undefined
  ): ValidationMessage[] | undefined => {
    if (validationMessages) {
      return validationMessages.filter(message => message.property !== property)
    } else {
      return undefined
    }
  }

  GetValidationMessagesForProperty = (
    property: string,
    validationMessages: ValidationMessage[] | undefined
  ): ValidationMessage[] => {
    if (validationMessages) {
      return validationMessages.filter(message =>
        message.property
          ? property.localeCompare(message.property, 'en', { sensitivity: 'base' }) === 0
          : false
      )
    }
    return []
  }

  id: string = 'newEvent'
  siteKey: string = ''
  scadaObjectKey: string = ''
  scadaObjectName: string = ''
  alarmCode: string = ''
  timeOn: Date
  timeOff?: Date
  comment: string | null = ''
  description: string = ''
  subGroup: string = ''
  group: string = ''
  penalisingStatus: string = ''
  lostProduction?: number
  meanWindSpeed?: number
  windSpeedDataCoverage?: number
  parentEventTimeOn?: Date
  saveInFlight: boolean = false
  doNotFilter: boolean = false
  failedAction?: FailedAction
  lockStatus: LockStatus = LockStatus.notLocked
  isNew: boolean = false
  lastModifiedBy: string = ''
  dateLastModified?: Date
  priority: number = 0
  validation?: ValidationResults
  canEditTimeOff?: boolean = undefined

  Edited: () => boolean
  IsValid: () => boolean
  GetAllValidationMessagesForProperty: (property: string) => ValidationMessage[]
  RemoveAllValidationMessagesForProperty: (property: string) => void
  AddValidationMessages: (validationMessages: ValidationMessage[]) => void
  SetValidationMessagesForProperties: (
    validationMessages: ValidationMessage[],
    properties: string[]
  ) => void
  HasBeenEdited: (original: DownTime) => boolean
  DurationInMiliSeconds: (toDate: Date, fromDate: Date) => number
}

export type AlarmGroup = {
  turbineBudget: boolean
  windfarmBudget: boolean
  operationalAvailability: boolean
  name: string
  technology: string
  subGroups: string[]
}

export class Alarm implements TimeOnOff {
  public constructor(init: Partial<Alarm>) {
    Object.assign(this, init)

    if (!init.timeOn) {
      throw new Error('Trying to create alarm with no timeon')
    }
    this.timeOn = new Date(init.timeOn)
    this.timeOff = init.timeOff ? new Date(init.timeOff) : undefined
  }

  timeOn: Date
  timeOff?: Date
  description: string = ''
  comment: string = ''
  alarmDefinitionCode: string = ''
}

export interface TimeOnOff {
  timeOn: Date
  timeOff?: Date
}
